import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  createBrowserRouter, LoaderFunctionArgs, Navigate, redirect,
  RouterProvider,
} from "react-router-dom";
import './index.css';
import App from './App';
import EmailLoginRedirect from "./pages/Login/EmailLoginRedirect";
import {getAuth, onAuthStateChanged} from "firebase/auth";
import LoginPage from "./pages/Login";
import AppContainer from "./components/AppContainer";
import SurveyPage from "./pages/Survey";
import DashboardPage from "./pages/Dashboard";
import PaymentCompleted from "./pages/PaymentCompleted";

import * as Sentry from "@sentry/react";
import {ColorsDefinitionPage} from "./pages/BackOffice/ColorsDefinition";

import Hotjar from '@hotjar/browser';

const siteId = import.meta.env.VITE_APP_HOTJAR_SITE_ID.trim();
const hotjarVersion = 6;

Hotjar.init(siteId, hotjarVersion);


if (import.meta.env.PROD) {
  Sentry.init({
    dsn: "https://82237480d238353d75534355ad408f5b@o4508071217790976.ingest.us.sentry.io/4508071220871168",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

const loginLoader = async () => {
  const auth = getAuth();
  return new Promise((resolve, reject) => {
    onAuthStateChanged(auth, (user) => {
      if (user !== null) {
        resolve(redirect("/"));
      } else {
        resolve(null);
      }
    }, reject);
  });
}

const protectedLoader = async ({ request }: LoaderFunctionArgs) => {
  // If the user is not logged in and tries to access `/protected`, we redirect
  // them to `/login` with a `from` parameter that allows login to redirect back
  // to this page upon successful authentication
  const auth = getAuth();
  return new Promise((resolve, reject) => {
    onAuthStateChanged(auth, (user) => {
      if (user === null) {

        let params = new URLSearchParams();
        params.set("from", new URL(request.url).pathname);
        resolve(redirect("/login?" + params.toString()));
      } else {
        resolve(null);
      }
    }, reject);
  });
}


const router = createBrowserRouter([
{
    path: "login",
    loader: loginLoader,
    element: <AppContainer><LoginPage/></AppContainer>,
  },
  {
    path: "/email-login-redirect",
    element: <AppContainer><EmailLoginRedirect/></AppContainer>,
  }, {
    path: "/back-office/colors-definition",
    element: <ColorsDefinitionPage/>,
    loader: protectedLoader,
  }, {
    path: "/payment-completed",
    element: <AppContainer><PaymentCompleted /></AppContainer>,
    loader: protectedLoader,
  }, {
    element: <App/>,
    children: [
      {
        path: "/",
        element: <DashboardPage />,
      }, {
        path: "/survey/canva-assets/all-features/business-info/:questionIdForNavigation",
        element: <SurveyPage
            deliverableGroupIdProp={"canva-assets"}
            featureIdProp={"all-features"}
            surveyIdProp={"business-info"}
        />,
      }, {
        path: "/survey/:deliverableGroupId/:featureIdForNavigation/:surveyIdForNavigation/:questionIdForNavigation",
        element: <SurveyPage />,
        loader: protectedLoader,
      }, {
        path: "*",
        element: <Navigate to="/" />,
      }
    ],
  }
]);


const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
);
root.render(
    <React.StrictMode>
      <RouterProvider router={router}/>
    </React.StrictMode>
);
